import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type { DataTableCellContext, DataTableColumn, DataTableRow } from '@/shared/ui/data-table/types';

/**
 * Turning column descriptors into TanStack column definitions.
 *
 * Separate from `use-data-table` because this is a pure translation with no
 * state in it at all, and mixing it in there meant the hook's own file grew
 * past what a reader can hold: the query lifecycle and the column vocabulary
 * are two different things.
 */

/** Renders a raw cell value as text, with an explicit empty marker. */
function defaultCell(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

/** Translates one declarative column descriptor into a TanStack column. */
function toColumnDef<TData extends DataTableRow>(
  column: DataTableColumn<TData>,
): ColumnDef<TData, unknown> {
  return {
    id: column.id,
    header: column.header,
    enableSorting: column.sortable ?? false,
    enableHiding: column.hideable ?? true,
    ...(column.sortDescFirst === undefined ? {} : { sortDescFirst: column.sortDescFirst }),
    meta: { sticky: column.sticky },
    accessorFn: (row: TData) => (column.accessorKey ? row[column.accessorKey] : undefined),
    cell: (context) =>
      column.cell
        ? column.cell({
            row: context.row.original,
            value: context.getValue(),
            variant: 'table',
          } as DataTableCellContext<TData>)
        : defaultCell(context.getValue()),
  };
}

/**
 * Serialises a value so it can be used as an effect dependency.
 *
 * Caller-supplied `filters` and `columns` are new objects on every render.
 * Keying on their contents gives a stable effect identity for a stable query,
 * which is what stops the fetch effect re-firing on unrelated renders.
 */
export function stableKey(value: unknown): string {
  return JSON.stringify(value ?? null);
}

/** The identity-bearing fields of a column, for content-keyed memoisation. */
function columnIdentity<TData extends DataTableRow>(column: DataTableColumn<TData>): unknown[] {
  return [
    column.id,
    column.header,
    column.sortable ?? false,
    column.sortDescFirst ?? null,
    column.hideable ?? true,
  ];
}

/**
 * The column set as TanStack definitions, rebuilt only when a column changes.
 *
 * Keyed by column CONTENT rather than array identity: callers build the column
 * array inline, so an identity dep would rebuild every cell renderer on every
 * render of the parent and defeat the memo entirely.
 */
export function useTableColumns<TData extends DataTableRow>(
  columns: DataTableColumn<TData>[],
): ColumnDef<TData, unknown>[] {
  const columnsKey = stableKey(columns.map(columnIdentity));
  return useMemo(
    () => columns.map(toColumnDef),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `columns` is addressed by its contents via columnsKey
    [columnsKey],
  );
}